/**
 * Frontend Error Handler
 * 
 * Utility for consistent error handling across the frontend application.
 * Provides structured error handling, user-friendly messages, and logging.
 */

/**
 * Error types for consistent error categorization
 */
export const ErrorType = {
  AUTHENTICATION: 'authentication',
  VALIDATION: 'validation',
  NETWORK: 'network',
  SERVER: 'server',
  PERMISSION: 'permission',
  NOT_FOUND: 'not_found',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown'
};

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical'
};

/**
 * Structured error class for consistent error handling
 */
export class AppError extends Error {
  constructor(type, message, severity = ErrorSeverity.ERROR, details = null) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.severity = severity;
    this.details = details;
    this.timestamp = new Date().toISOString();
    
    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
  
  /**
   * Convert to plain object for serialization
   */
  toObject() {
    return {
      name: this.name,
      type: this.type,
      message: this.message,
      severity: this.severity,
      details: this.details,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }
  
  /**
   * Get user-friendly error message
   */
  getUserMessage() {
    // First, check if we have a specific error message from the backend
    // The details might contain the actual response data
    if (this.details && this.details.responseData) {
      const responseData = this.details.responseData;
      // Try to extract error message from response data
      if (responseData.error && typeof responseData.error === 'string' && responseData.error.trim()) {
        return responseData.error;
      }
      if (responseData.message && typeof responseData.message === 'string' && responseData.message.trim()) {
        return responseData.message;
      }
      if (responseData.detail && typeof responseData.detail === 'string' && responseData.detail.trim()) {
        return responseData.detail;
      }
    }
    
    // Next, try to use the message property (already extracted from response)
    if (this.message && this.message !== 'An error occurred' && this.message !== 'An unexpected error occurred') {
      return this.message;
    }
    
    // Fallback to type-specific messages if no specific message
    const messages = {
      [ErrorType.AUTHENTICATION]: {
        [ErrorSeverity.INFO]: 'Please login to continue',
        [ErrorSeverity.WARNING]: 'Your session has expired',
        [ErrorSeverity.ERROR]: 'Authentication failed',
        [ErrorSeverity.CRITICAL]: 'Security violation detected'
      },
      [ErrorType.VALIDATION]: {
        [ErrorSeverity.INFO]: 'Please check your input',
        [ErrorSeverity.WARNING]: 'Some fields need attention',
        [ErrorSeverity.ERROR]: 'Invalid input provided',
        [ErrorSeverity.CRITICAL]: 'Data validation failed'
      },
      [ErrorType.NETWORK]: {
        [ErrorSeverity.INFO]: 'Connection issue detected',
        [ErrorSeverity.WARNING]: 'Network is unstable',
        [ErrorSeverity.ERROR]: 'Network connection failed',
        [ErrorSeverity.CRITICAL]: 'Network unavailable'
      },
      [ErrorType.SERVER]: {
        [ErrorSeverity.INFO]: 'Server is processing your request',
        [ErrorSeverity.WARNING]: 'Server is experiencing issues',
        [ErrorSeverity.ERROR]: 'Server error occurred',
        [ErrorSeverity.CRITICAL]: 'Server unavailable'
      },
      [ErrorType.PERMISSION]: {
        [ErrorSeverity.INFO]: 'Additional permissions required',
        [ErrorSeverity.WARNING]: 'Access restricted',
        [ErrorSeverity.ERROR]: 'Permission denied',
        [ErrorSeverity.CRITICAL]: 'Access violation'
      },
      [ErrorType.NOT_FOUND]: {
        [ErrorSeverity.INFO]: 'Resource not found',
        [ErrorSeverity.WARNING]: 'Resource unavailable',
        [ErrorSeverity.ERROR]: 'Requested resource not found',
        [ErrorSeverity.CRITICAL]: 'Critical resource missing'
      },
      [ErrorType.TIMEOUT]: {
        [ErrorSeverity.INFO]: 'Request taking longer than expected',
        [ErrorSeverity.WARNING]: 'Operation timeout',
        [ErrorSeverity.ERROR]: 'Request timed out',
        [ErrorSeverity.CRITICAL]: 'System timeout'
      },
      [ErrorType.UNKNOWN]: {
        [ErrorSeverity.INFO]: 'An issue occurred',
        [ErrorSeverity.WARNING]: 'Something went wrong',
        [ErrorSeverity.ERROR]: 'An error occurred',
        [ErrorSeverity.CRITICAL]: 'A critical error occurred'
      }
    };
    
    // Get type-specific message or fallback to generic
    const typeMessages = messages[this.type];
    if (typeMessages && typeMessages[this.severity]) {
      return typeMessages[this.severity];
    }
    
    // Fallback messages if type/severity not found
    const fallbackMessages = {
      [ErrorSeverity.INFO]: 'An issue occurred',
      [ErrorSeverity.WARNING]: 'Something went wrong',
      [ErrorSeverity.ERROR]: 'An error occurred',
      [ErrorSeverity.CRITICAL]: 'A critical error occurred'
    };
    
    return fallbackMessages[this.severity] || 'An unexpected error occurred';
  }
}

/**
 * Error handler utility class
 */
class ErrorHandler {
  constructor() {
    this.listeners = new Set();
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }
  
  /**
   * Register error listener
   */
  addListener(listener) {
    this.listeners.add(listener);
  }
  
  /**
   * Remove error listener
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }
  
  /**
   * Handle error with structured approach
   */
  handleError(error, context = {}) {
    // Convert to AppError if not already
    const appError = this.normalizeError(error);
    
    // Add context to error
    appError.context = context;
    
    // Log error based on severity
    this.logError(appError);
    
    // Notify listeners
    this.notifyListeners(appError);
    
    // Return structured error for further handling
    return appError;
  }
  
  /**
   * Normalize any error to AppError
   */
  normalizeError(error) {
    // Handle null or undefined error
    if (error === null || error === undefined) {
      return new AppError(
        ErrorType.UNKNOWN,
        'An unexpected error occurred - no error details available',
        ErrorSeverity.ERROR,
        { originalError: null }
      );
    }
    
    if (error instanceof AppError) {
      return error;
    }
    
    // Handle axios errors
    if (error.isAxiosError) {
      return this.handleAxiosError(error);
    }
    
    // Handle HTTP response errors
    if (error.response) {
      return this.handleHttpError(error);
    }
    
    // Handle network errors
    if (error.request && !error.response) {
      return new AppError(
        ErrorType.NETWORK,
        'Network error: Unable to connect to server',
        ErrorSeverity.ERROR,
        { originalError: error.message }
      );
    }
    
    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return new AppError(
        ErrorType.TIMEOUT,
        'Request timeout: The operation took too long',
        ErrorSeverity.WARNING,
        { originalError: error.message }
      );
    }
    
    // Handle validation errors
    if (error.name === 'ValidationError' || error.errors) {
      return new AppError(
        ErrorType.VALIDATION,
        error.message || 'Validation error',
        ErrorSeverity.WARNING,
        { originalError: error }
      );
    }
    
    // Default to unknown error
    return new AppError(
      ErrorType.UNKNOWN,
      error.message || 'An unexpected error occurred',
      ErrorSeverity.ERROR,
      { originalError: error }
    );
  }
  
  /**
   * Handle axios-specific errors
   */
  handleAxiosError(error) {
    const { response, request, code, message } = error;
    
    if (response) {
      return this.handleHttpResponseError(response);
    }
    
    if (request && !response) {
      return new AppError(
        ErrorType.NETWORK,
        'Network error: No response received from server',
        ErrorSeverity.ERROR,
        { code, message }
      );
    }
    
    if (code === 'ECONNABORTED') {
      return new AppError(
        ErrorType.TIMEOUT,
        'Request timeout: The server took too long to respond',
        ErrorSeverity.WARNING,
        { code, message }
      );
    }
    
    return new AppError(
      ErrorType.UNKNOWN,
      message || 'Network request failed',
      ErrorSeverity.ERROR,
      { code, message }
    );
  }
  
  /**
   * Handle HTTP response errors
   */
  handleHttpError(error) {
    const { response } = error;
    return this.handleHttpResponseError(response);
  }
  
  /**
   * Handle HTTP response with error status
   */
  handleHttpResponseError(response) {
    const { status, data, config } = response;
    const { url, method } = config;
    
    console.log('[DEBUG] ErrorHandler.handleHttpResponseError: Called', {
      status,
      url,
      method,
      data,
      dataType: typeof data,
      dataKeys: data && typeof data === 'object' ? Object.keys(data) : 'N/A'
    });
    
    // Map HTTP status codes to error types
    const statusMappings = {
      400: { type: ErrorType.VALIDATION, severity: ErrorSeverity.WARNING },
      401: { type: ErrorType.AUTHENTICATION, severity: ErrorSeverity.WARNING },
      403: { type: ErrorType.PERMISSION, severity: ErrorSeverity.WARNING },
      404: { type: ErrorType.NOT_FOUND, severity: ErrorSeverity.INFO },
      409: { type: ErrorType.VALIDATION, severity: ErrorSeverity.WARNING },
      422: { type: ErrorType.VALIDATION, severity: ErrorSeverity.WARNING },
      429: { type: ErrorType.SERVER, severity: ErrorSeverity.WARNING },
      500: { type: ErrorType.SERVER, severity: ErrorSeverity.ERROR },
      502: { type: ErrorType.SERVER, severity: ErrorSeverity.ERROR },
      503: { type: ErrorType.SERVER, severity: ErrorSeverity.ERROR },
      504: { type: ErrorType.SERVER, severity: ErrorSeverity.ERROR }
    };
    
    const mapping = statusMappings[status] || { 
      type: ErrorType.SERVER, 
      severity: ErrorSeverity.ERROR 
    };
    
    console.log('[DEBUG] ErrorHandler.handleHttpResponseError: Status mapping', {
      status,
      mapping
    });
    
    // Extract error message from response
    let errorMessage = 'An error occurred';
    if (data) {
      if (typeof data === 'string') {
        errorMessage = data;
        console.log('[DEBUG] ErrorHandler.handleHttpResponseError: Extracted error from string data:', errorMessage);
      } else if (data.error) {
        errorMessage = data.error;
        console.log('[DEBUG] ErrorHandler.handleHttpResponseError: Extracted error from data.error:', errorMessage);
      } else if (data.message) {
        errorMessage = data.message;
        console.log('[DEBUG] ErrorHandler.handleHttpResponseError: Extracted error from data.message:', errorMessage);
      } else if (data.detail) {
        errorMessage = data.detail;
        console.log('[DEBUG] ErrorHandler.handleHttpResponseError: Extracted error from data.detail:', errorMessage);
      } else {
        console.log('[DEBUG] ErrorHandler.handleHttpResponseError: No error message found in data, using default');
      }
    } else {
      console.log('[DEBUG] ErrorHandler.handleHttpResponseError: No data in response, using default error message');
    }
    
    const appError = new AppError(
      mapping.type,
      errorMessage,
      mapping.severity,
      {
        status,
        url,
        method,
        responseData: data,
        timestamp: new Date().toISOString()
      }
    );
    
    console.log('[DEBUG] ErrorHandler.handleHttpResponseError: Created AppError', {
      type: appError.type,
      message: appError.message,
      severity: appError.severity,
      hasGetUserMessage: typeof appError.getUserMessage === 'function',
      getUserMessage: appError.getUserMessage ? appError.getUserMessage() : 'N/A'
    });
    
    return appError;
  }
  
  /**
   * Log error based on severity
   */
  logError(error) {
    const { severity, message, details, context } = error;
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      severity,
      message,
      details,
      context,
      stack: this.isDevelopment ? error.stack : undefined
    };
    
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        console.error('🔴 CRITICAL ERROR:', logEntry);
        // In production, send to error tracking service
        this.sendToErrorTracking(logEntry);
        break;
        
      case ErrorSeverity.ERROR:
        console.error('🔴 ERROR:', logEntry);
        break;
        
      case ErrorSeverity.WARNING:
        console.warn('🟡 WARNING:', logEntry);
        break;
        
      case ErrorSeverity.INFO:
        console.info('🔵 INFO:', logEntry);
        break;
        
      default:
        console.log('⚪ LOG:', logEntry);
    }
  }
  
  /**
   * Send error to error tracking service (stub for implementation)
   */
  sendToErrorTracking(error) {
    // In a real application, this would send to Sentry, LogRocket, etc.
    if (this.isDevelopment) {
      console.debug('Error tracking (development):', error);
    }
    // Implementation for production would go here
  }
  
  /**
   * Notify all registered listeners
   */
  notifyListeners(error) {
    for (const listener of this.listeners) {
      try {
        listener(error);
      } catch (listenerError) {
        console.error('Error listener failed:', listenerError);
      }
    }
  }
  
  /**
   * Create user-friendly error message for display
   */
  createUserMessage(error) {
    const appError = this.normalizeError(error);
    
    // Use error's user message if available
    if (appError.getUserMessage) {
      return appError.getUserMessage();
    }
    
    // Fallback to generic messages based on type
    const genericMessages = {
      [ErrorType.AUTHENTICATION]: 'Please check your login credentials and try again.',
      [ErrorType.VALIDATION]: 'Please check your input and try again.',
      [ErrorType.NETWORK]: 'Please check your internet connection and try again.',
      [ErrorType.SERVER]: 'The server is experiencing issues. Please try again later.',
      [ErrorType.PERMISSION]: 'You don\'t have permission to perform this action.',
      [ErrorType.NOT_FOUND]: 'The requested resource was not found.',
      [ErrorType.TIMEOUT]: 'The request took too long. Please try again.',
      [ErrorType.UNKNOWN]: 'An unexpected error occurred. Please try again.'
    };
    
    return genericMessages[appError.type] || 'An error occurred. Please try again.';
  }
  
  /**
   * Check if error is recoverable
   */
  isRecoverable(error) {
    const appError = this.normalizeError(error);
    
    // Non-recoverable errors
    const nonRecoverableTypes = [
      ErrorType.PERMISSION,
      ErrorType.NOT_FOUND
    ];
    
    const nonRecoverableSeverities = [
      ErrorSeverity.CRITICAL
    ];
    
    return !(
      nonRecoverableTypes.includes(appError.type) ||
      nonRecoverableSeverities.includes(appError.severity)
    );
  }
  
  /**
   * Get retry recommendation for error
   */
  getRetryRecommendation(error) {
    const appError = this.normalizeError(error);
    
    const retryableTypes = [
      ErrorType.NETWORK,
      ErrorType.TIMEOUT,
      ErrorType.SERVER
    ];
    
    if (retryableTypes.includes(appError.type)) {
      return {
        shouldRetry: true,
        delay: 2000, // 2 seconds
        maxRetries: 3
      };
    }
    
    return {
      shouldRetry: false,
      reason: `Error type ${appError.type} is not retryable`
    };
  }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

// Export singleton and classes
export { errorHandler };
export default errorHandler;

// Convenience functions
export function handleError(error, context = {}) {
  return errorHandler.handleError(error, context);
}

export function getUserMessage(error) {
  return errorHandler.createUserMessage(error);
}

export function isRecoverableError(error) {
  return errorHandler.isRecoverable(error);
}

export function getRetryRecommendation(error) {
  return errorHandler.getRetryRecommendation(error);
}
